import xmlFormat from 'xml-formatter';

import {
  BaseField,
  DocumentDefinition,
  DocumentDefinitionType,
  DocumentType,
  IDocument,
  IField,
  IParentType,
  PrimitiveDocument,
} from '../../models/datamapper/document';
import {
  FieldItem,
  InstructionItem,
  MappingItem,
  MappingParentType,
  MappingTree,
  OtherwiseItem,
  UnknownMappingItem,
  ValueOfSelector,
  ValueOfType,
  VariableItem,
} from '../../models/datamapper/mapping';
import { DeserializeItemResult, DeserializeResult, MappingItemClass } from '../../models/datamapper/serialization';
import { NS_XML_SCHEMA_INSTANCE, NS_XSL } from '../../models/datamapper/standard-namespaces';
import { DEFAULT_DATAMAPPER_SETTINGS, FieldOverrideVariant, IDataMapperSettings } from '../../models/datamapper/types';
import { SendAlertProps } from '../../models/datamapper/visualization';
import { QName } from '../../xml-schema-ts/QName';
import { DocumentUtilService } from '../document/document-util.service';
import { FieldOverrideService } from '../document/field-override.service';
import { FROM_JSON_SOURCE_SUFFIX, JsonSchemaDocument } from '../document/json-schema/json-schema-document.model';
import { XmlSchemaDocumentUtilService } from '../document/xml-schema/xml-schema-document-util.service';
import { ensureNamespaceRegistered, parseQNameString } from '../namespace-util';
import { MappingService } from './mapping.service';
import { MappingSerializerJsonAddon, TO_JSON_TARGET_VARIABLE } from './mapping-serializer-json-addon';
import { deserializeHandlers, serializeHandlers } from './xslt-item-handlers';

export const DO_NOT_EDIT_COMMENT = '<!-- This file is generated by Kaoto DataMapper. Do not edit. -->';

export function getEmptyXsl(dataMapperSettings?: IDataMapperSettings): string {
  const xslOutputAttrs = [
    `method="xml"`,
    `indent="yes"`,
    dataMapperSettings?.omitXmlDeclaration && `omit-xml-declaration="yes"`,
  ]
    .filter(Boolean)
    .join(' ');

  return `<?xml version="1.0" encoding="UTF-8"?>
${DO_NOT_EDIT_COMMENT}
<xsl:stylesheet version="3.0" xmlns:xsl="${NS_XSL}">
  <xsl:output ${xslOutputAttrs}/>
</xsl:stylesheet>
`;
}

export const EMPTY_XSL = getEmptyXsl();

export class MappingSerializerService {
  static createNew(dataMapperSettings?: IDataMapperSettings) {
    return new DOMParser().parseFromString(getEmptyXsl(dataMapperSettings), 'application/xml');
  }

  private static sortFields(left: IField, right: IField) {
    return left.parent.fields.indexOf(left) - right.parent.fields.indexOf(right);
  }

  private static getMappingItemFields(item: MappingItem): IField[] {
    if (item instanceof FieldItem) return [item.field];
    if (item instanceof InstructionItem) return MappingService.getInstructionFields(item);
    return [];
  }

  private static hasAttributeField(fields: IField[]): boolean {
    return fields.some((f) => f.isAttribute);
  }

  private static compareSpecialMappingItems(left: MappingItem, right: MappingItem): number | null {
    if (left instanceof VariableItem) return right instanceof VariableItem ? 0 : -1;
    if (right instanceof VariableItem) return 1;
    if (left instanceof UnknownMappingItem || right instanceof UnknownMappingItem) return 0;
    if (left instanceof OtherwiseItem) return right instanceof OtherwiseItem ? 0 : 1;
    if (right instanceof OtherwiseItem) return -1;
    if (left instanceof InstructionItem && right instanceof InstructionItem) return 0;
    return null;
  }

  private static compareMappingItemFields(leftFields: IField[], rightFields: IField[]) {
    if (leftFields.length === 0) return rightFields.length === 0 ? 0 : 1;
    if (rightFields.length === 0) return -1;

    const leftHasAttribute = MappingSerializerService.hasAttributeField(leftFields);
    const rightHasAttribute = MappingSerializerService.hasAttributeField(rightFields);

    if (leftHasAttribute !== rightHasAttribute) return leftHasAttribute ? -1 : 1;
    if (leftHasAttribute && rightHasAttribute) return 0;

    const leftFirst = [...leftFields].sort(MappingSerializerService.sortFields)[0];
    const rightFirst = [...rightFields].sort(MappingSerializerService.sortFields)[0];
    return leftFirst.parent.fields.indexOf(leftFirst) - rightFirst.parent.fields.indexOf(rightFirst);
  }

  private static sortMappingItem(left: MappingItem, right: MappingItem) {
    const specialComparison = MappingSerializerService.compareSpecialMappingItems(left, right);
    if (specialComparison !== null) return specialComparison;

    const leftFields = MappingSerializerService.getMappingItemFields(left);
    const rightFields = MappingSerializerService.getMappingItemFields(right);

    return MappingSerializerService.compareMappingItemFields(leftFields, rightFields);
  }

  /**
   * Serialize the {@link MappingTree} model object into an XSLT mappings document string.
   * @param mappings {@link MappingTree} object to write
   * @param sourceParameterMap source paramter map
   * @param dataMapperSettings DataMapper settings
   */
  static serialize(
    mappings: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
    dataMapperSettings?: IDataMapperSettings,
  ): string {
    const xslt = MappingSerializerService.createNew(dataMapperSettings);
    const outputNamespaceURIs = MappingSerializerService.collectOutputNamespaceURIs(mappings);
    for (const namespaceURI of outputNamespaceURIs) {
      ensureNamespaceRegistered(
        namespaceURI,
        mappings.namespaceMap,
        namespaceURI === NS_XML_SCHEMA_INSTANCE ? 'xsi' : undefined,
      );
    }
    MappingSerializerService.populateNamespaces(xslt, mappings.namespaceMap, outputNamespaceURIs);
    MappingSerializerService.populateParam(xslt, sourceParameterMap);
    MappingSerializerService.populateStylesheetVariables(xslt, mappings);
    const root = MappingSerializerService.populateMappingRoot(xslt, mappings);
    MappingSerializerService.populateTemplateVariables(root, mappings);
    [...mappings.children].sort(MappingSerializerService.sortMappingItem).forEach((mapping) => {
      MappingSerializerService.populateMapping(root, mapping);
    });
    return xmlFormat(new XMLSerializer().serializeToString(xslt));
  }

  private static populateNamespaces(
    xslt: Document,
    namespaceMap: { [prefix: string]: string },
    outputNamespaceURIs: Set<string> = new Set(),
  ) {
    const rootElement = xslt.documentElement;
    const prefixes: string[] = [];
    Object.entries(namespaceMap).forEach(([prefix, uri]) => {
      if (prefix && uri) {
        rootElement.setAttribute(`xmlns:${prefix}`, uri);
        // Prefixes whose namespace URIs appear in the output must NOT be excluded —
        // the XSLT processor needs them to emit the correct namespace declarations.
        if (!outputNamespaceURIs.has(uri)) {
          prefixes.push(prefix);
        }
      }
    });
    // These prefixes are only declared for matching the source document via XPath.
    // Without `exclude-result-prefixes` they leak into the output as redundant
    // namespace declarations, which some consumers (e.g. HAPI HL7 v2 XMLParser)
    // fail to parse. `exclude-result-prefixes` only suppresses declarations that
    // are not actually used by an output element, so this is safe.
    if (prefixes.length > 0) {
      rootElement.setAttribute('exclude-result-prefixes', prefixes.join(' '));
    }
  }

  /** Collects namespace URIs that must appear in the output (i.e. must not be excluded). */
  private static collectOutputNamespaceURIs(mappings: MappingTree): Set<string> {
    const uris = new Set<string>();
    const allRoots = [...mappings.children, ...mappings.globalVariables];
    MappingSerializerService.walkMappingItems(allRoots, (item) => {
      if (item instanceof FieldItem && item.field.typeOverride === FieldOverrideVariant.SAFE && item.field.typeQName) {
        uris.add(NS_XML_SCHEMA_INSTANCE);
        const typeNsURI = item.field.typeQName.getNamespaceURI();
        if (typeNsURI) uris.add(typeNsURI);
      }
    });
    return uris;
  }

  private static walkMappingItems(items: MappingItem[], visitor: (item: MappingItem) => void) {
    for (const item of items) {
      visitor(item);
      if (item.children.length > 0) {
        MappingSerializerService.walkMappingItems(item.children, visitor);
      }
    }
  }

  private static getRootStyleSheet(xsltDocument: Document) {
    const prefix = xsltDocument.lookupPrefix(NS_XSL);
    // The document is used both as the XPath context node and as the namespace resolver.
    return xsltDocument
      .evaluate(`/${prefix}:stylesheet`, xsltDocument, xsltDocument, XPathResult.ANY_TYPE)
      .iterateNext()! as Element;
  }

  private static populateMappingRoot(xsltDocument: Document, mappings: MappingTree) {
    const template = xsltDocument.createElementNS(NS_XSL, 'template');
    template.setAttribute('match', '/');
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    stylesheet.appendChild(template);

    const jsonRoot = MappingSerializerJsonAddon.populateJsonTargetBase(mappings, template);
    if (jsonRoot) return jsonRoot;

    return template;
  }

  private static populateParam(xsltDocument: Document, sourceParameterMap: Map<string, IDocument>) {
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    sourceParameterMap.forEach((doc, paramName) => {
      const prefix = xsltDocument.lookupPrefix(NS_XSL);
      // The document is used both as the XPath context node and as the namespace resolver.
      const existing = xsltDocument
        .evaluate(
          `/${prefix}:stylesheet/${prefix}:param[@name='${paramName}']`,
          xsltDocument,
          xsltDocument,
          XPathResult.ANY_TYPE,
        )
        .iterateNext();
      if (existing) return;

      const xsltParam = xsltDocument.createElementNS(NS_XSL, 'param');
      xsltParam.setAttribute('name', paramName);
      stylesheet.appendChild(xsltParam);

      MappingSerializerJsonAddon.populateJsonToXmlVariable(doc, stylesheet, paramName);
    });
  }

  private static populateStylesheetVariables(xsltDocument: Document, mappings: MappingTree) {
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    for (const gv of mappings.globalVariables) {
      if (gv.scope !== 'stylesheet') continue;
      if (gv.rawElement) {
        stylesheet.appendChild(xsltDocument.importNode(gv.rawElement, true));
      } else {
        MappingSerializerService.populateMapping(stylesheet, gv);
      }
    }
  }

  private static populateTemplateVariables(templateRoot: Element, mappings: MappingTree) {
    for (const gv of mappings.globalVariables) {
      if (gv.scope !== 'template') continue;
      MappingSerializerService.populateMapping(templateRoot, gv);
    }
  }

  private static populateMapping(parent: Element, mapping: MappingItem) {
    // Add comment before the element if it exists
    if (mapping.comment) {
      const xsltDocument = parent.ownerDocument;
      const comment = xsltDocument.createComment(` ${mapping.comment} `);
      const sanitized = comment.data.replace(/--/g, '- -');
      comment.data = sanitized;
      parent.appendChild(comment);
    }

    const handler = serializeHandlers.get(mapping.constructor as MappingItemClass);
    const child = handler?.serialize(parent, mapping) ?? null;
    if (!child) {
      return;
    }

    for (const childItem of [...mapping.children].sort(MappingSerializerService.sortMappingItem)) {
      MappingSerializerService.populateMapping(child, childItem);
    }
  }

  /**
   * Deserialize the XSLT mappings document into a {@link MappingTree} model object.
   * @param xslt XSLT mappings document in string format
   * @param targetDocument Target Document
   * @param mappingTree {@link MappingTree} object to write
   * @param sourceParameterMap source parameter map
   */
  static deserialize(
    xslt: string,
    targetDocument: IDocument,
    mappingTree: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
  ): DeserializeResult {
    mappingTree.children = [];
    mappingTree.globalVariables = [];
    const messages: SendAlertProps[] = [];
    const xsltDoc = new DOMParser().parseFromString(xslt, 'application/xml');
    const dataMapperSettings = MappingSerializerService.restoreDataMapperSettings(xsltDoc);
    const template = xsltDoc.getElementsByTagNameNS(NS_XSL, 'template')[0];
    if (!template?.children) return { mappingTree, messages, dataMapperSettings };
    MappingSerializerService.restoreNamespaces(xsltDoc, mappingTree);
    MappingSerializerService.restoreParam(xsltDoc, sourceParameterMap);
    MappingSerializerService.restoreStylesheetVariables(xsltDoc, mappingTree, sourceParameterMap, messages);
    const root = MappingSerializerJsonAddon.getJsonTargetBase(xsltDoc, mappingTree) ?? template;
    for (const item of Array.from(root.childNodes)) {
      MappingSerializerService.restoreMapping(item, targetDocument, mappingTree, messages);
    }
    return { mappingTree, messages, dataMapperSettings };
  }

  private static restoreDataMapperSettings(xsltDocument: Document): IDataMapperSettings {
    const output = xsltDocument.getElementsByTagNameNS(NS_XSL, 'output')[0];

    return {
      ...DEFAULT_DATAMAPPER_SETTINGS,
      omitXmlDeclaration: output?.getAttribute('omit-xml-declaration') === 'yes',
    };
  }

  private static restoreNamespaces(xsltDocument: Document, mappingTree: MappingTree) {
    mappingTree.namespaceMap = {};
    const rootElement = xsltDocument.documentElement;
    Array.from(rootElement.attributes).forEach((attr) => {
      if (!attr.nodeName.includes(':') || !attr.nodeValue || attr.nodeValue === NS_XSL) return;
      const splitted = attr.nodeName.split(':');
      if (splitted[0] === 'xmlns') mappingTree.namespaceMap[splitted[1]] = attr.nodeValue;
    });
  }

  private static restoreParam(xsltDocument: Document, sourceParameterMap: Map<string, IDocument>) {
    const prefix = xsltDocument.lookupPrefix(NS_XSL);
    // The document is used both as the XPath context node and as the namespace resolver.
    const params = xsltDocument.evaluate(
      `/${prefix}:stylesheet/${prefix}:param`,
      xsltDocument,
      xsltDocument,
      XPathResult.ANY_TYPE,
    );
    let param: Node | null;
    while ((param = params.iterateNext())) {
      const paramEl = param as Element;
      const name = paramEl.getAttribute('name');
      if (!name || sourceParameterMap.has(name)) continue;
      sourceParameterMap.set(
        name,
        new PrimitiveDocument(new DocumentDefinition(DocumentType.PARAM, DocumentDefinitionType.Primitive, name)),
      );
    }
  }

  private static restoreStylesheetVariables(
    xsltDocument: Document,
    mappingTree: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
    messages: SendAlertProps[],
  ) {
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    for (const child of Array.from(stylesheet.children)) {
      if (child.namespaceURI !== NS_XSL || child.localName !== 'variable') continue;
      const varName = child.getAttribute('name')?.trim() || '';
      if (!varName) {
        messages.push({
          variant: 'danger',
          title: 'Skipping stylesheet-level xsl:variable without a name',
          description: new XMLSerializer().serializeToString(child),
        });
        continue;
      }
      if (varName === TO_JSON_TARGET_VARIABLE || varName.endsWith(FROM_JSON_SOURCE_SUFFIX)) {
        if (!MappingSerializerService.isDataMapperReservedVariable(varName, mappingTree, sourceParameterMap)) {
          messages.push({
            variant: 'danger',
            title: `Skipping reserved stylesheet-level variable name: "${varName}"`,
            description: new XMLSerializer().serializeToString(child),
          });
        }
        continue;
      }
      if (sourceParameterMap.has(varName)) continue;
      const variable = new VariableItem(mappingTree, varName);
      variable.scope = 'stylesheet';
      const selectAttr = child.getAttribute('select');
      const requiresRawElement =
        child.hasChildNodes() || Array.from(child.attributes).some((a) => a.name !== 'name' && a.name !== 'select');
      if (requiresRawElement) {
        variable.rawElement = child.cloneNode(true) as Element;
        variable.expression = selectAttr || '';
      } else {
        variable.expression = selectAttr || '';
      }
      mappingTree.globalVariables.push(variable);
    }
  }

  private static isDataMapperReservedVariable(
    varName: string,
    mappingTree: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
  ): boolean {
    if (varName === TO_JSON_TARGET_VARIABLE) {
      return mappingTree.documentDefinitionType === DocumentDefinitionType.JSON_SCHEMA;
    }
    const baseName = varName.slice(0, -FROM_JSON_SOURCE_SUFFIX.length);
    const sourceDoc = sourceParameterMap.get(baseName);
    return sourceDoc instanceof JsonSchemaDocument;
  }

  private static restoreMapping(
    item: Node,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ) {
    if (item.nodeType === Node.TEXT_NODE) {
      MappingSerializerService.restoreTextNode(item, parentMapping);
    } else if (item.nodeType === Node.ELEMENT_NODE) {
      MappingSerializerService.restoreElementNode(item as Element, parentField, parentMapping, messages);
    }
  }

  private static restoreTextNode(item: Node, parentMapping: MappingParentType) {
    const literal = (item.textContent || '').trim();
    if (!literal) return;
    const selector = new ValueOfSelector(parentMapping, ValueOfType.VALUE);
    selector.expression = literal;
    selector.isLiteral = true;
    parentMapping.children.push(selector);
  }

  private static restoreCommentFromPreviousSibling(element: Element, mappingItem: MappingItem) {
    // Look for preceding comment node
    let previousSibling = element.previousSibling;
    while (previousSibling?.nodeType === Node.TEXT_NODE) {
      previousSibling = previousSibling.previousSibling;
    }
    if (previousSibling?.nodeType === Node.COMMENT_NODE) {
      mappingItem.comment = (previousSibling as Comment).data.trim();
    }
  }

  private static isUserCreatedField(field: IField): boolean {
    if (field.typeOverride === FieldOverrideVariant.SUBSTITUTION) return true;
    if (field.typeOverride === FieldOverrideVariant.SAFE) return true;

    let current: IParentType = field.parent;
    while ('ownerDocument' in current) {
      const f = current;
      if (f.wrapperKind === 'abstract' && f.selectedMemberQName !== undefined) return true;
      if (f.wrapperKind === 'choice' && f.selectedMemberIndex !== undefined) return true;
      if (f.wrapperKind) {
        current = f.parent;
        continue;
      }
      break;
    }

    return false;
  }

  private static restoreElementNode(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ) {
    const result =
      element.namespaceURI === NS_XSL
        ? MappingSerializerService.restoreXslElement(element, parentField, parentMapping, messages)
        : MappingSerializerService.restoreNonXslElement(element, parentField, parentMapping);

    if (!result?.mappingItem) return;

    if (result.mappingItem instanceof FieldItem) {
      const hasContent = Array.from(element.childNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()),
      );
      if (!hasContent || MappingSerializerService.isUserCreatedField(result.mappingItem.field)) {
        result.mappingItem.isUserCreated = true;
      }
    }

    if (result.mappingItem instanceof VariableItem && parentMapping instanceof MappingTree) {
      result.mappingItem.scope = 'template';
      parentMapping.globalVariables.push(result.mappingItem);
    } else {
      parentMapping.children.push(result.mappingItem);
    }

    const isXslText = element.namespaceURI === NS_XSL && element.localName === 'text';
    if (!(result.mappingItem instanceof UnknownMappingItem) && !isXslText) {
      for (const childItem of Array.from(element.childNodes)) {
        MappingSerializerService.restoreMapping(
          childItem,
          result.fieldItem ?? parentField,
          result.mappingItem,
          messages,
        );
      }
    }
  }

  private static restoreNonXslElement(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
  ): { mappingItem: MappingItem; fieldItem: IParentType | null } | null {
    if (parentField instanceof PrimitiveDocument) return null;
    const field = MappingSerializerService.getOrCreateElementField(element, parentField);
    if (!field) return null;

    // Preserve xsi:type literal attribute for round-trip support.
    // Only apply when the field has no existing schema-driven override.
    const xsiType = element.getAttributeNS(NS_XML_SCHEMA_INSTANCE, 'type');
    if (xsiType && field.typeOverride === FieldOverrideVariant.NONE) {
      MappingSerializerService.applyXsiTypeOverride(field, xsiType, element);
    }

    const mappingItem = new FieldItem(parentMapping, field);
    MappingSerializerService.restoreCommentFromPreviousSibling(element, mappingItem);
    return { fieldItem: field, mappingItem };
  }

  private static applyXsiTypeOverride(field: IField, xsiTypeValue: string, element: Element): void {
    const { prefix, localPart } = parseQNameString(xsiTypeValue);
    const namespaceURI = element.lookupNamespaceURI(prefix || null) ?? '';
    const typeQName = new QName(namespaceURI, localPart, prefix);

    field.originalField ??= DocumentUtilService.captureOriginalFieldState(field);
    field.typeQName = typeQName;
    field.typeOverride = FieldOverrideVariant.SAFE;
  }

  private static restoreXslElement(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ): DeserializeItemResult<MappingItem> | null {
    const handler = deserializeHandlers.get(element.localName);
    let result: DeserializeItemResult<MappingItem> | null;
    if (handler) {
      result = handler.deserialize(element, parentField, parentMapping) as DeserializeItemResult<MappingItem> | null;
    } else {
      result = {
        mappingItem: new UnknownMappingItem(parentMapping, element),
        fieldItem: null,
        messages: [
          {
            variant: 'warning',
            title: `Unrecognized XSLT element: "xsl:${element.localName}"`,
            description: new XMLSerializer().serializeToString(element),
          },
        ],
      };
    }
    if (!result) return null;
    if (result.messages) {
      messages.push(...result.messages);
    }
    if (result.mappingItem) {
      MappingSerializerService.restoreCommentFromPreviousSibling(element, result.mappingItem);
    }

    return result;
  }

  private static getOrCreateElementField(item: Element, parentField: IParentType): IField {
    const jsonField = MappingSerializerJsonAddon.getOrCreateJsonField(item, parentField);
    if (jsonField) return jsonField;

    const namespace = item.namespaceURI || '';
    const name = item.localName;
    const existing = XmlSchemaDocumentUtilService.getChildField(parentField, name, namespace);
    if (existing) return existing;

    const substitutionMatch = FieldOverrideService.findSubstitutionGroupHead(parentField, name, namespace);
    if (substitutionMatch?.headField.maxOccurs === 1) {
      DocumentUtilService.applySubstitutionToField(substitutionMatch.headField, substitutionMatch.info);
      return substitutionMatch.headField;
    }

    const field = new BaseField(
      parentField,
      'ownerDocument' in parentField ? parentField.ownerDocument : parentField,
      name,
    );
    field.namespaceURI = namespace;
    parentField.fields.push(field);
    return field;
  }
}
